<template>
  <div>
    <!-- loading overlay -->
    <div class="py-5" v-if="isLoading">
      <v-overlay color="white" absolute opacity="1">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      </v-overlay>
    </div>
    <div v-else>
      <template>
        <div class="pt-3 pb-0 d-flex justify-space-between align-center black--text">
          <div>
            <div style="font-size: 20px;" class="mb-2">
              {{ $t('accounting_product.type_of_list') }} :
              <span class="text-red-500" v-if="formData.isEscaped">{{ $t('contract.escaped') }}</span>
              <span class="text-red-500" v-else-if="formData.isCloseContract">{{ $t('contract.close_contract') }}</span>
              <span class="text-yellow-500" v-else>{{ $t('contract.reduce_area') }}</span>
            </div>
            <div style="font-size: 18px;">
              {{ $t('market.market_title') }} :
              <span class="text-picton-blue-600">
                {{
                  formData.areas
                  ? formData.areas.map((i) => i.name).join(", ")
                  : "-"
                }}
              </span>
            </div>
          </div>
          <div class="d-flex align-center">
            <div style="font-size: 20px;">{{ $t('status') }} : </div>
            <div>
              <span 
                :class="`px-3 block py-2 ms-2 text-center rounded-full ${formData.isPaidClass.chipCss}`"
              >
                {{ formData.isPaidClass.text }}
              </span>
            </div>
          </div>
        </div>
        <AlertCard class="mt-3" color="warning">
          <template v-slot:title>
            <div>{{ $t('payment.unpaid_bill_list') }}</div>
          </template>
          <template v-slot:content>
            <div class="pt-5 pb-4 px-5">
              <InvoiceTable :itemsProps="formData.unpaidInvoicesTable" @onRowClick="onRowClick" />
              <div :class="`flex justify-center py-3 text-xl rounded ${formData.unpaidTotalCost > 0
                ? 'text-red-600 bg-red-100'
                : formData.unpaidTotalCost == 0
                  ? 'text-gray-400'
                  : 'success--text bg-green-100'
                }`">
                {{ formData.isEscaped && formData.unpaidTotalCost ? $t('bill.bad_debt') : $t('payment.remaining_pending') }}
                {{ formData.unpaidTotalCost | currencySuffixTextThEn }}
              </div>
            </div>
          </template>
        </AlertCard>

        <AlertCard class="mt-3" color="warning">
          <template v-slot:title>
            <div class="d-flex justify-space-between align-center w-100">
              <div>{{ $t('bill.checkout_bill_list_detail') }}</div>
              <v-btn v-if="_method == 'edit'" color="info" elevation="0" class="ml-2" @click="onCreateInvoice"
                :disabled="enableCannotCheckOut">
                <v-icon>mdi-plus</v-icon>{{ $t('bill.add_checkout_bill') }}
              </v-btn>
            </div>
          </template>
          <template v-slot:content>
            <div class="pt-5 pb-4 px-5">
              <InvoiceTable :itemsProps="formData.checkOutInvoicesTable" @onRowClick="onRowClick" />
              <div :class="`flex justify-center py-3 text-xl rounded ${formData.checkOutTotalCost > 0
                ? 'text-red-600 bg-red-100'
                : formData.checkOutTotalCost == 0
                  ? 'text-gray-400'
                  : 'success--text bg-green-100'
                }`">
                {{ formData.isEscaped && formData.checkOutTotalCost  ? $t('bill.bad_debt') : $t('payment.remaining_pending') }}
                {{ formData.checkOutTotalCost | currencySuffixTextThEn }}
              </div>
            </div>
          </template>
        </AlertCard>

        <AlertCard class="mt-3" color="warning">
          <template v-slot:title>
            <div>{{ $t('payment.deposit_bill_list') }}</div>
          </template>
          <template v-slot:content>
            <div class="pt-5 pb-4 px-5">
              <InvoiceTable :itemsProps="formData.depositInvoicesTable" @onRowClick="onRowClick" />
              <div :class="`flex justify-center py-3 text-xl rounded ${formData.depositTotalCost > 0
                ? 'success--text bg-green-100'
                : formData.depositTotalCost == 0
                  ? 'text-gray-400'
                  : 'text-red-600 bg-red-100'
                }`">
                {{ $t('payment.deposit_cost') }}
                {{ formData.depositTotalCost | currencySuffixTextThEn }}
              </div>
              <template v-if="!formData.isEscaped">
                <p class="text-sm text-center text-gray-400 mt-8 mb-0">{{ $t('validate.in_the_case_of_reducing_the_stall_only_the_stall_will_be_calculated') }}</p>
                <div class="flex justify-center mb-4">
                  <v-switch 
                    v-model="isForfeit" 
                    :disabled="invoiceCheckout.depositTotalCost == 0 || _method != 'edit' || enableCannotCheckOut"
                    color="success"
                    :value="false" 
                    hide-details
                  >
                    <template v-slot:label>
                      <span class="text-gray-400">{{ $t('payment.want_to_forfeit_deposit') }}</span>
                    </template>
                  </v-switch>
                </div>
              </template>
            </div>
          </template>
        </AlertCard>

        <AlertCard class="mt-3" color="warning" v-if="isForfeit && !formData.isEscaped">
          <template v-slot:title>
            <div>{{ $t('payment.forfeit_deposit_list') }}</div>
          </template>
          <template v-slot:content>
            <div class="pt-5 pb-4 px-5">
              <div>
                <v-radio-group v-model="modeForfeitSelect" row hide-details class="mt-2" disabled>
                  <div class="d-flex justify-space-around w-100 gap-3" style="width: 100%;">
                    <v-radio :label="$t('payment.forfeit_all')" :value="1" class="box-radio"></v-radio>
                    <v-radio :label="$t('payment.enter_amount')" :value="2" class="box-radio"></v-radio>
                    <v-radio :label="$t('payment.select_from_list')" :value="3" class="box-radio"></v-radio>
                  </div>
                </v-radio-group>
              </div>
              <div class="border border-gray-400 rounded px-5 py-3 mt-4">
                <div v-if="modeForfeitSelect != 3" class="d-flex justify-space-between align-center gap-3">
                  <div>{{ $t('product.quantity') }}</div>
                  <div class="w-100" style="width: 100%;">
                    <v-currency-field
                      v-model="forfeitCost" 
                      class="mt-0 pt-0 input-right" 
                      outlined
                      :min="0"
                      :max="formData.depositTotalCost"
                      disabled 
                      hide-details
                      :rules="[rules.required]"
                      dense
                    />
                  </div>
                  <div>{{ $helper.currencyThEn() }}</div>
                </div>
                <div v-else-if="modeForfeitSelect == 3">
                  <div v-for="invoice of formData.depositInvoices" :key="invoice.documentId">
                    <div class="forfeit-invoice-header"> {{ invoice.documentId }}</div>
                    <v-checkbox class="forfeit-item" v-for="(item, index) of invoice.items"
                      v-model="item.isForfeit"
                      color="success"
                      :key="`${item.name}-${index}`"
                      hide-details
                      disabled
                    >
                      <template v-slot:label>
                        <span class="mx-3">{{ `${item.name} ${item.detail ? '(' + item.detail + ')' : ''}` }}</span>
                        <span class="mx-3">{{ `${item.area ? `${$t('area.title')} ${item.area.name}` : `${$t('area.not_specify_stall')}`}` }}</span>
                        <span class="mx-3">{{ `${$helper.toCurrency(item.totalValue)}` }}</span>
                      </template>
                    </v-checkbox>
                  </div>
                </div>
                <v-textarea
                  class="mt-3"
                  :placeholder="$t('validate.please_input_reason_for_forfeiting_deposit')"
                  no-resize
                  :label="$t('payment.reason_for_forfeit')"
                  outlined
                  v-model="forfeitNote"
                  rows="3"
                  :rules="[rules.required]"
                  hide-details
                  disabled
                />
                <div :class="`flex justify-center py-3 mt-3 text-xl text-web-orange-600 bg-web-orange-100 rounded`">
                  {{ $t('payment.forfeit_deposit_money_value') }}
                  {{ forfeitCost | currencySuffixTextThEn }}
                </div>
              </div>
            </div>
          </template>
        </AlertCard>

        <AlertCard class="mt-3" color="info">
          <template v-slot:title>
            <div>{{ $t('area.summary_checkout') }}</div>
          </template>
          <template v-slot:content>
            <div class="pt-7 pb-4 px-5">
              <div v-if="formData.isEscaped" class="d-flex  gap-3">
                <BoxCheckoutSummary 
                  color="error" 
                  :totalValue="formData.unpaidTotalCost + formData.checkOutTotalCost" 
                  :title="$t('bill.bad_debt')"
                />
                <BoxCheckoutSummary 
                  color="success" 
                  :totalValue="formData.depositTotalCost" 
                  :title="$t('contract.deposit_received')"
                />
              </div>
              <div v-else>
                <BoxCheckoutSummary 
                  :color="calCulateInvoice.color" 
                  :totalValue="formData.totalCostAbs" 
                  :title="`${calCulateInvoice.text}`"
                />
              </div>
              <CheckoutDetail />
            </div>
          </template>
        </AlertCard>

        <div class="py-3 mt-5">
          <v-menu ref="menu_check_out_date" v-model="isCheckOutDate" :close-on-content-click="false"
            :return-value.sync="checkOutDate" min-width="auto" :disabled="_method != 'edit' || enableCannotCheckOut">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field :value="$helper.datelabel(checkOutDate)" :label="$t('area.checkout_date')"
                prepend-inner-icon="mdi-calendar" readonly outlined v-bind="attrs" v-on="on"
                :disabled="_method != 'edit' || enableCannotCheckOut" hide-details dense></v-text-field>
            </template>
            <v-date-picker v-model="checkOutDate" no-title scrollable locale="th"
              :header-date-format="$helper.headerDateFormat" :year-format="$helper.yearFormat" persistent-hint
              :weekday-format="$helper.formatWeekday" :month-format="$helper.formatMonth" :disabled="_method != 'edit' || enableCannotCheckOut">
              <v-spacer></v-spacer>
              <v-btn text color="error" @click="isCheckOutDate = false"
                :disabled="_method != 'edit' || enableCannotCheckOut">{{ $t('cancel') }}</v-btn>
              <v-btn text color="success" :disabled="_method != 'edit' || enableCannotCheckOut"
                @click="$refs.menu_check_out_date.save(checkOutDate);">{{ $t('confirm') }}</v-btn>
            </v-date-picker>
          </v-menu>
        </div>
        <div class="py-3">
          <span>{{ $t('project.image_or_file_if_have') }}</span>
          <ImageUpload v-model="imageFiles" :isLoading="isLoading" :isView="_method != 'edit' || enableCannotCheckOut" refName="fileupload" />
        </div>
        <div class="py-3">
          <v-text-field :label="$t('note')" textarea outlined flat v-model="formData.note"
            :disabled="_method != 'edit' || enableCannotCheckOut" hide-details dense></v-text-field>
        </div>
        <div class="py-4" v-if="formData.totalCost > 0 && _method == 'edit' && !enableCannotCheckOut">
          <span>{{ $t('payment.select_bank_account_to_refund_deposit') }}</span>
          <v-select :items="formData.contract.customer.bankAccounts" v-model="formData.refundBankAccount" :item-text="(i) =>
            `${$t('account.title')}${getBank(i.bankCode)} ${$t('payment.account_number_label')} ${i.accountNumber
            } ${$t('payment.account_name')}  ${i.accountName}`
            " :item-value="(i) => i" return-object :label="$t('payment.bank_account')" solo flat
            background-color="grey lighten-4" :no-data-text="$t('noinfo')" dense></v-select>
        </div>
        <div class="p-5 rounded-lg shadow-lg mt-4" v-if="formData.totalCost > 0 && _method != 'edit'">
          <div class="text-lg font-bold">{{ $t('account.refund_bank_account') }}</div>
          <template v-if="formData.refundBankAccount && Object.keys(formData.refundBankAccount).length > 0">
            {{
              `${$t('account.title')} ${getBank(
                formData.refundBankAccount.bankCode
              )} ${$t('payment.account_number_label')} ${formData.refundBankAccount.accountNumber || "-"
                } ${$t('payment.account_name')} ${formData.refundBankAccount.accountName || "-"
                }`
            }}
          </template>
          <template v-else>-</template>
        </div>
        <div class="flex justify-space-between align-items-end">
          <div style="align-self: end;" class="mt-5">
            <div class="my-2" v-if="formData.creditNotes.length">
              {{ $t('credit_note.title') }} :
              <template v-for="creditNote in formData.creditNotes">
                <v-chip color="info" @click="clickCreditNote(creditNote)">
                  {{ creditNote.documentId }}
                </v-chip>
              </template>
            </div>
            <div class="my-2" v-if="!isSentBillCheckout && !enableCannotCheckOut">
              {{ $t('bill.checkout_bill_list') }} :
              <span class="text-red-500">{{ $t('bill.checkout_bill_not_send') }}</span>
            </div>
            <div class="my-2" v-if="isForfeit && !formData.isEscaped && (!forfeitNote || !forfeitCost)">
              {{ $t('payment.forfeit_deposit_list') }} :
              <span class="text-red-500">{{ $t('validate.must_input_reason_for_forfeit') }}</span>
            </div>
          </div>
          <div class="pt-10">
            <v-btn outlined color="default" class="mr-2" elevation="0" @click="onDismissCheckout"
              v-if="_method == 'edit'">{{ $t('close') }}</v-btn>
            <template></template>
            <v-btn color="success" elevation="0" @click="isConfirmDialog = true" :loading="isLoading"
              v-if="_method == 'edit' && isClearDebt && formData.status != 'draft' && formData.status != 'rejected' && formData.approveStatus != 'rejected'">{{ $t('confirm_that') }}{{
                this.formData.isCloseContract
                ? $t('contract.close_contract') : $t('contract.reduce_area') }}</v-btn>
            <v-btn color="success" elevation="0" @click="onSaveCheckout()" :loading="isLoading"
              v-if="_method == 'edit' && !isClearDebt"
              :disabled="isForfeit && (!forfeitNote || !forfeitCost)">{{ $t('save') }}</v-btn>
            <v-btn v-if="formData.status == 'draft' && formData.approveStatus == 'draft'" class="mr-2" color="error"
              elevation="0" @click="cancelDraftContractCheckoutFn" :loading="isLoading">{{ $t('area.cancel_draft') }}</v-btn>
            <v-btn v-if="formData.status == 'draft' && formData.approveStatus == 'draft'" color="info" elevation="0"
              @click="isConfirmSendDialog = true"
              :disabled="enableCannotCheckOut || !isSentBillCheckout || (isForfeit && (!forfeitNote || !forfeitCost))"
              :loading="isLoading">{{ $t('appeal.send_appeal') }}</v-btn>
          </div>
        </div>
      </template>
      <InvoiceDialogComponent v-if="getProjectId" :projectId="getProjectId" :documentType="4"
        :contractData="formData ? formData.contract : null" v-model="isInvoiceDialog" :invoiceId="invoiceId"
        :canSend="!enableCannotCheckOut" @onSaveSuccess="updateInvoice" @onRemoveSuccess="updateInvoice" />
      <!-- loading dialog -->
      <v-dialog v-model="isCheckoutLoading" persistent max-width="500px">
        <v-card height="250">
          <!-- v-overlay -->
          <v-overlay color="white" :absolute="true">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </v-overlay>
        </v-card>
      </v-dialog>
      <ConfirmDialogComponent
        v-model="isConfirmDialog"
        :title="$t('payment.confirm_transaction')"
        :text="$t('validate.cannot_edit_data_after_confirm')"
        textClass="text-red-500" 
        @onOk="onSaveCheckout(true)"
        :textBtnOk="formData.isCloseContract ? $t('contract.close_contract') : $t('contract.confirm_reduce_area')"
        :textBtnCancel="$t('cancel')"
        :isConfirmCheck="true"
        type="warning"
        :delay="5"
      />
      <ConfirmDialogComponent
        v-model="isConfirmSendDialog" :title="$t('payment.confirm_transaction')"
        :text="$t('validate.cannot_edit_data_after_confirm')"
        textClass="text-red-500"
        @onOk="sendApproveTask()" 
        :textBtnOk="$t('appeal.send_appeal')"
        :delay="5"
      />
      <CreditNoteDialog v-if="tempCreditNote" v-model="isCreditNoteDialog" :creditNoteId="tempCreditNote.objectId"
        :projectId="getProjectId" />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import { Banking, ContractCheckout } from "../classes";
import InvoiceTable from "./InvoiceTable.vue";
import InvoiceDialogComponent from "./InvoiceDialog.component.vue";
import ConfirmDialogComponent from "./ConfirmDialog.component.vue";
import CheckoutDetail from "./CheckoutDetail.vue";
import AlertCard from "./AlertCard.component.vue";
import BoxCheckoutSummary from "./BoxCheckoutSummary.component.vue";
import ImageUpload from "./ImageUpload.vue";
import CreditNoteDialog from "@/components/CreditNoteDialog.component.vue";
export default {
  components: {
    InvoiceTable,
    InvoiceDialogComponent,
    ConfirmDialogComponent,
    CheckoutDetail,
    ImageUpload,
    CreditNoteDialog,
    AlertCard,
    BoxCheckoutSummary
  },
  computed: {
    ...mapGetters({
      contractAreas: "contract/getAreas",
      invoiceCheckout: "contractCheckout/contractCheckout",
      getProjectId: "markets/getProjectId",
      getContract: "contract/getContract",
      getContractId: "contract/getContractId",
      customerBanking: "customer/getCustomerBanking",
      checkouts: "contractCheckout/checkouts",
      adminGetByMarketId: "admin/getByMarketId",
      employeeGetByMarketId: "employee/getByMarketId",
    }),
    employee() {
      return this.employeeGetByMarketId(this.marketId);
    },
    enableCannotCheckOut() {
      return this.employee && this.employee.enableCannotCheckOut;
    },
    calCulateInvoice() {
      let depositTotalCost = this.formData.depositTotalCost - (this.contrctCheckOut.isForfeit ? parseFloat(this.contrctCheckOut.forfeitCost) : 0)
      const total = depositTotalCost - (this.formData.unpaidTotalCost + this.formData.checkOutTotalCost);
      return {
        total: Math.abs(total),
        text: total > 0 ? this.$t('payment.refund_customer_money') : this.$t('payment.collect_customer_money'),
        color: total > 0 ? 'info' : total < 0 ? 'error' : 'success'
      };
    },
    isSentBillCheckout() {
      let res = true;
      for (const checkOutInvoice of this.formData.checkOutInvoices) {
        if (!checkOutInvoice.isSent) {
          res = false;
          break;
        }
      }
      return res;
    }
  },
  watch: {
    value(val) {
      if (val == "checkout") {
        this.isCloseContract = true;
      } else {
        this.isCloseContract = false;
      }
    },
    formData: {
      handler(formData) {
        let isError = false;
        if (
          formData &&
          formData.checkOutInvoices &&
          formData.checkOutInvoices.length
        ) {
          for (const checkOutInvoice of formData.checkOutInvoices) {
            if (!checkOutInvoice.isSent && !checkOutInvoice.isCancelled) {
              isError = true;
              break;
            }
          }
        }
        if (isError) this.$emit("onError", this.$t('bill.checkout_bill_not_send'));
        else this.$emit("onError", "");
      },
      deep: true,
      immediate: true
    }
  },
  created() { },
  props: {
    contractCheckOutId: String,
    method: String,
    isClearDebt: Boolean
  },
  data() {
    return {
      rules: {
        required: i => !!i || this.$t('validate.please_input')
      },
      marketId: this.$route.params.marketId,
      value: "checkout",
      form: {
        areaIds: [],
        customerBanking: {}
      },
      isCloseContract: false,
      isNextStep: false,
      isInvoiceDialog: false,
      areaIds: [],
      isLoading: false,
      firstStep: false,
      isCheckoutLoading: false,
      isAlert: false,
      confirmItem: {},
      isConfirmDialog: false,
      isConfirmSendDialog: false,
      isView: false,
      invoiceId: null,
      checkOutDate: this.$moment().format("YYYY-MM-DD"),
      leaveDate: this.$moment().format("YYYY-MM-DD"),
      isCheckOutDate: false,
      isLeaveDatePicker: false,
      contrctCheckOut: null,
      formData: null,
      _method: "edit",
      isShowCal: false,

      isForfeit: false,
      modeForfeitSelect: 1,
      forfeitCost: 0,
      forfeitNote: '',
      isFirst: true,
      imageFiles:[],
      isCreditNoteDialog: false,
      tempCreditNote: null,
    };
  },
  created() {
    this.initData();
  },
  methods: {
    ...mapMutations({
      setContractCheckoutForfeit: 'contractCheckout/setContractCheckoutForfeit'
    }),
    ...mapActions({
      getContractCheckout: "contractCheckout/getContractCheckout",
      getContractCheckoutData: "contractCheckout/getContractCheckoutData",
      updateContractCheckout: "contractCheckout/updateContractCheckout",
      sendContractCheckoutApproveTask: "contractCheckout/sendContractCheckoutApproveTask",
      cancelDraftContractCheckout: "contractCheckout/cancelDraftContractCheckout"
    }),
    async initData() {
      try {
        this.isLoading = true;
        this._method = this.method;
        this.contrctCheckOut = await this.getContractCheckout({ id: this.contractCheckOutId });
        if (
          (this.contrctCheckOut.isSendApproveTask &&
            (this.contrctCheckOut.approveStatus == "rejected" ||
              this.contrctCheckOut.approveStatus == "approved")) ||
          this.contrctCheckOut.status == "rejected"
        ) {
          this._method = "view";
        }

        this.isForfeit = this.contrctCheckOut.isForfeit || false
        this.modeForfeitSelect = this.contrctCheckOut.modeForfeitSelect || 1
        this.forfeitCost = this.contrctCheckOut.forfeitCost || 0
        this.forfeitNote = this.contrctCheckOut.forfeitNote || ''
        this.imageFiles = this.contrctCheckOut.imageFiles || []

        this.checkOutDate = this.$moment(
          this.contrctCheckOut.checkOutDate
        ).format("YYYY-MM-DD");
        this.formData = this.contrctCheckOut;
        console.log(this.formData);
        this.isLoading = false;
      } catch (e) {
        console.log(e);
      }
    },
    onPreConfirm() {
      this.isConfirmDialog = true;
    },
    onRowClick(e) {
      this.invoiceId = e.objectId;
      this.isInvoiceDialog = true;
    },
    momentDate(date) {
      return this.$moment(date).format("DD MMMM YYYY");
    },
    getBank(code) {
      return Banking.getBank(code)?.name || "-";
    },
    onCreateInvoice() {
      this.invoiceId = null;
      this.isInvoiceDialog = true;
    },
    async updateInvoice() {
      try {
        let contrctCheckOutData = await this.getContractCheckoutData({
          contractId: this.contrctCheckOut.contract.objectId,
          isCloseContract: this.contrctCheckOut.isCloseContract,
          areaIds: this.contrctCheckOut.areas.map(d => d.objectId),
          contractCheckOutId: this.contrctCheckOut.objectId
        }
        );

        //Reset Forfeit
        this.isForfeit = false
        this.modeForfeitSelect = 1
        this.forfeitCost = 0
        this.forfeitNote = ''

        this.formData.unpaidTotalCost = contrctCheckOutData.unpaidTotalCost;
        this.formData.checkOutTotalCost = contrctCheckOutData.checkOutTotalCost;
        this.formData.depositTotalCost = contrctCheckOutData.depositTotalCost;

        this.formData.unpaidInvoices = contrctCheckOutData.unpaidInvoices;
        this.formData.checkOutInvoices = contrctCheckOutData.checkOutInvoices;
        this.formData.depositInvoices = contrctCheckOutData.depositInvoices;

        this.formData = new ContractCheckout(this.formData);
        const params = this.prepareData();
        await this.updateContractCheckout(params);
        this.$emit("onUpdateInvoice", this.invoiceCheckout);
      } catch (e) {
        console.log(e);
      }
    },
    async sendApproveTask() {
      try {
        this.isLoading = true;
        let params = this.prepareData();
        let res = await this.sendContractCheckoutApproveTask(params);
        this.$emit("onSaveSuccess");
        this.$emit("onClose");
        this.isLoading = false;
      } catch (e) {
        console.log(e);
      }
    },
    async cancelDraftContractCheckoutFn() {
      try {
        this.isLoading = true;
        let params = this.prepareData();
        let res = await this.cancelDraftContractCheckout(params);
        this.$emit("onSaveSuccess");
        this.$emit("onClose");
        this.isLoading = false;
      } catch (e) {
        console.log(e);
      }
    },
    prepareData() {
      let totalCost = ((this.formData.depositTotalCost || 0) - (this.invoiceCheckout.isForfeit ? this.invoiceCheckout.forfeitCost || 0 : 0)) - (this.formData.checkOutTotalCost + this.formData.unpaidTotalCost)
      return {
        id: this.formData.objectId,
        unpaidTotalCost: this.formData.unpaidTotalCost,
        checkOutTotalCost: this.formData.checkOutTotalCost,
        depositTotalCost: this.formData.depositTotalCost,
        forfeitCost: this.invoiceCheckout.isForfeit ? this.invoiceCheckout.forfeitCost : 0,
        modeForfeitSelect: this.invoiceCheckout.modeForfeitSelect,
        isForfeit: this.invoiceCheckout.isForfeit,
        forfeitNote: this.forfeitNote,
        checkOutInvoices: this.formData.checkOutInvoices,
        depositInvoices: this.invoiceCheckout.depositInvoices,
        unpaidInvoices: this.formData.unpaidInvoices,
        refundCost: totalCost > 0 ? Math.abs(totalCost) : 0,
        extraCost: totalCost < 0 ? Math.abs(totalCost) : 0,
        note: this.formData.note,
        refundBankAccount: this.formData.refundBankAccount,
        checkOutDate: this.$moment(this.checkOutDate, "YYYY-MM-DD")
          .endOf("day")
          .toISOString(),
        leaveDate: this.$moment(this.leaveDate, "YYYY-MM-DD")
          .endOf("day")
          .toISOString(),
        status: this.formData.status,
        approveStatus: this.formData.approveStatus,
        imageFiles : this.imageFiles
      };
    },
    onViewDetail() {
      this.isView = true;
      this.firstStep = false;
      this.isNextStep = true;
    },
    async onSaveCheckout(isClearDebt) {
      try {
        this.isLoading = true;
        const params = this.prepareData();
        await this.updateContractCheckout(params);
        if (isClearDebt) {
          await ContractCheckout.clearDebtContractCheckout(params.id);
        }
        // await this.initData();
        this.$emit("onSaveSuccess", this.invoiceCheckout);
        this.$emit("onClose");
        this.isLoading = false;
      } catch (e) {
        console.log(e);
      }
    },
    onDismissCheckout() {
      this.$emit("onClose");
      this.isNextStep = false;
    },
    clickCreditNote(creditNote) {
      this.tempCreditNote = creditNote
      this.isCreditNoteDialog = true
    }
  }
};
</script>

<style lang="scss" scoped>
.flex-center {
  display: flex;
  justify-content: center;
  align-items: center;

  div {
    margin: 0 5px;
    font-size: 18px;
  }
}

.dialog-checkout-in.v-card__text::v-deep {
  height: 90vh;
  max-height: calc(90vh - 65px - 103px);
}

.forfeit-invoice-header {
  font-weight: bold;
  margin-bottom: 5px;
  color: black;
  font-size: 18px;
  margin-top: 10px;
}

.forfeit-item {
  background-color: white;
  border-radius: 10px;
  width: 100%;
  padding: 10px;
  margin-top: 5px;
}

.forfeit-box {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 0px;

  .forfeit-box-in {
    .forfeit-invoice {
      .forfeit-invoice-header {
        font-weight: bold;
        margin-bottom: 5px;
        margin-top: 10px;
      }

      .forfeit-item {
        margin: 0;
        padding: 0;
        padding-left: 15px;
      }
    }
  }
}

.theme--light.v-expansion-panels .v-expansion-panel {
  background-color: unset;
}

.v-expansion-panel::before {
  box-shadow: none;
}

.v-application--is-ltr .v-expansion-panel-header {
  text-align: right;
}

.checkkout-warning {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin: 10px 15px;
  padding: 15px;
  border: 1px solid var(--v-warning-base);
  border-radius: 15px;

  div {
    font-size: 20px;
  }

  .v-chip {
    margin: 0 5px;
  }
}

.box-radio {
  padding: 15px 10px;
  border: 1px solid #A9A9A9;
  border-radius: 8px;
  width: 100%;
  margin: 0px !important;

  &.v-item--active {
    border: 1px solid var(--v-fontPrimary-base);
  }

  &.v-radio--is-disabled {
    border: 1px solid #A9A9A9;
    opacity: 0.5;
  }
}
</style>